/**
 * Unified ResolutionSelector Component
 *
 * Flexible resolution selector that supports:
 * - API-loaded presets (image generation)
 * - Multiplier-based presets (thumbnail export)
 * - Custom dimensions
 * - Grouped resolutions
 *
 * Consolidates: image/ResolutionSelector, thumbnail-studio/export/ResolutionSelector
 */

import { useState, useMemo, useCallback, memo } from 'react';
import { Monitor, Smartphone, Square, Film, Share2, type LucideIcon } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';
import { bytesToMB } from '@/constants/fileSize';

// ============================================================================
// Types
// ============================================================================

export interface Resolution {
  id: string;
  label: string;
  width: number;
  height: number;
  aspectRatio?: string;
  category?: string;
}

export interface ResolutionGroup {
  name: string;
  resolutions: Resolution[];
}

export interface MultiplierPreset {
  id: string;
  label: string;
  multiplier: number;
}

export type ResolutionMode = 'presets' | 'multipliers' | 'grouped';

export interface BaseResolutionSelectorProps {
  /** Current selected value (resolution id or multiplier id) */
  value: string;
  /** Callback when resolution changes */
  onChange: (
    id: string,
    width: number,
    height: number,
    metadata?: { multiplier?: number; category?: string }
  ) => void;
  /** Whether to allow custom dimensions */
  allowCustom?: boolean;
  /** Custom width (when value is 'custom') */
  customWidth?: number;
  /** Custom height (when value is 'custom') */
  customHeight?: number;
  /** Callback for custom dimension changes */
  onCustomChange?: (width: number, height: number) => void;
  /** Whether component is disabled */
  disabled?: boolean;
  /** Additional class name */
  className?: string;
  /** Whether to show dimension info below selection */
  showDimensionInfo?: boolean;
  /** Min/max constraints for custom dimensions */
  customConstraints?: {
    minWidth?: number;
    maxWidth?: number;
    minHeight?: number;
    maxHeight?: number;
    step?: number;
  };
}

// Presets mode (like image generation)
export interface PresetModeProps extends BaseResolutionSelectorProps {
  mode: 'presets';
  /** Static preset resolutions */
  presets: Resolution[];
}

// Grouped mode (like image generation with categories)
export interface GroupedModeProps extends BaseResolutionSelectorProps {
  mode: 'grouped';
  /** Grouped resolutions by category */
  groups: ResolutionGroup[];
}

// Multiplier mode (like thumbnail export)
export interface MultiplierModeProps extends BaseResolutionSelectorProps {
  mode: 'multipliers';
  /** Multiplier presets (0.5x, 1x, 2x, etc.) */
  multipliers: MultiplierPreset[];
  /** Base canvas dimensions for multiplier calculation */
  canvasWidth: number;
  canvasHeight: number;
  /** Whether to maintain aspect ratio for custom */
  maintainAspectRatio?: boolean;
}

export type ResolutionSelectorProps = PresetModeProps | GroupedModeProps | MultiplierModeProps;

// ============================================================================
// Constants
// ============================================================================

const CATEGORY_ICONS: Record<string, LucideIcon> = {
  square: Square,
  landscape: Monitor,
  portrait: Smartphone,
  social: Share2,
  cinema: Film,
};

const DEFAULT_CONSTRAINTS = {
  minWidth: 100,
  maxWidth: 8000,
  minHeight: 100,
  maxHeight: 8000,
  step: 1,
};

// ============================================================================
// Helper Components
// ============================================================================

interface ResolutionButtonProps {
  resolution: { id: string; label: string; width: number; height: number };
  isSelected: boolean;
  onClick: () => void;
  disabled: boolean;
  showDimensions?: boolean;
}

const ResolutionButton = memo(function ResolutionButton({
  resolution,
  isSelected,
  onClick,
  disabled,
  showDimensions = true,
}: ResolutionButtonProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex flex-col items-center gap-1 p-2 rounded-lg border transition-all',
        'hover:border-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20',
        'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        isSelected
          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
          : 'border-secondary-200 dark:border-secondary-700 text-secondary-600 dark:text-secondary-400'
      )}
    >
      <span className="font-semibold text-sm">{resolution.label}</span>
      {showDimensions && (
        <span className="text-xs text-secondary-500 dark:text-secondary-400">
          {resolution.width}x{resolution.height}
        </span>
      )}
    </button>
  );
});

// ============================================================================
// Main Component
// ============================================================================

export const ResolutionSelector = memo(function ResolutionSelector(props: ResolutionSelectorProps) {
  const { t } = useTranslation();
  const {
    value,
    onChange,
    allowCustom = true,
    customWidth,
    customHeight,
    onCustomChange,
    disabled = false,
    className = '',
    showDimensionInfo = true,
    customConstraints = DEFAULT_CONSTRAINTS,
  } = props;

  const isCustom = value === 'custom';
  const [localCustomWidth, setLocalCustomWidth] = useState(customWidth || 1920);
  const [localCustomHeight, setLocalCustomHeight] = useState(customHeight || 1080);

  // Merge constraints with defaults (memoized to avoid recreating on every render)
  const constraints = useMemo(
    () => ({ ...DEFAULT_CONSTRAINTS, ...customConstraints }),
    [customConstraints]
  );

  // Calculate dimensions based on mode
  const getDimensions = useCallback(
    (id: string): { width: number; height: number } | null => {
      if (id === 'custom') {
        return {
          width: customWidth || localCustomWidth,
          height: customHeight || localCustomHeight,
        };
      }

      if (props.mode === 'multipliers') {
        const preset = props.multipliers.find((p) => p.id === id);
        if (preset) {
          return {
            width: Math.round(props.canvasWidth * preset.multiplier),
            height: Math.round(props.canvasHeight * preset.multiplier),
          };
        }
      } else if (props.mode === 'presets') {
        const preset = props.presets.find((p) => p.id === id);
        if (preset) {
          return { width: preset.width, height: preset.height };
        }
      } else if (props.mode === 'grouped') {
        for (const group of props.groups || []) {
          const res = group.resolutions?.find((r) => r.id === id);
          if (res) {
            return { width: res.width, height: res.height };
          }
        }
      }

      return null;
    },
    [props, customWidth, customHeight, localCustomWidth, localCustomHeight]
  );

  // Get current dimensions
  const currentDimensions = useMemo(() => getDimensions(value), [getDimensions, value]);

  // Handle preset selection
  const handleSelect = useCallback(
    (id: string, metadata?: { multiplier?: number; category?: string }) => {
      const dims = getDimensions(id);
      if (dims) {
        onChange(id, dims.width, dims.height, metadata);
      }
    },
    [getDimensions, onChange]
  );

  // Handle custom dimension changes
  const handleCustomWidthChange = useCallback(
    (newWidth: number) => {
      const clampedWidth = Math.max(constraints.minWidth, Math.min(constraints.maxWidth, newWidth));

      let newHeight = localCustomHeight;

      // Maintain aspect ratio if specified (multiplier mode)
      if (props.mode === 'multipliers' && props.maintainAspectRatio) {
        const aspectRatio = props.canvasWidth / props.canvasHeight;
        newHeight = Math.round(clampedWidth / aspectRatio);
      }

      setLocalCustomWidth(clampedWidth);
      setLocalCustomHeight(newHeight);
      onCustomChange?.(clampedWidth, newHeight);
      onChange('custom', clampedWidth, newHeight);
    },
    [constraints, localCustomHeight, onChange, onCustomChange, props]
  );

  const handleCustomHeightChange = useCallback(
    (newHeight: number) => {
      const clampedHeight = Math.max(
        constraints.minHeight,
        Math.min(constraints.maxHeight, newHeight)
      );

      let newWidth = localCustomWidth;

      // Maintain aspect ratio if specified (multiplier mode)
      if (props.mode === 'multipliers' && props.maintainAspectRatio) {
        const aspectRatio = props.canvasWidth / props.canvasHeight;
        newWidth = Math.round(clampedHeight * aspectRatio);
      }

      setLocalCustomWidth(newWidth);
      setLocalCustomHeight(clampedHeight);
      onCustomChange?.(newWidth, clampedHeight);
      onChange('custom', newWidth, clampedHeight);
    },
    [constraints, localCustomWidth, onChange, onCustomChange, props]
  );

  // Render based on mode
  const renderPresets = () => {
    if (props.mode === 'multipliers') {
      const presets = props.multipliers.map((m) => ({
        id: m.id,
        label: m.label,
        ...getDimensions(m.id)!,
      }));

      return (
        <div className="grid grid-cols-4 gap-2">
          {presets.map((preset) => (
            <ResolutionButton
              key={preset.id}
              resolution={preset}
              isSelected={value === preset.id}
              onClick={() =>
                handleSelect(preset.id, {
                  multiplier: props.multipliers.find((m) => m.id === preset.id)?.multiplier,
                })
              }
              disabled={disabled}
            />
          ))}
          {allowCustom && (
            <ResolutionButton
              resolution={{
                id: 'custom',
                label: t('common.custom', 'Custom'),
                width: localCustomWidth,
                height: localCustomHeight,
              }}
              isSelected={isCustom}
              onClick={() => handleSelect('custom')}
              disabled={disabled}
            />
          )}
        </div>
      );
    }

    if (props.mode === 'presets') {
      return (
        <div className="grid grid-cols-3 gap-2">
          {props.presets.map((preset) => (
            <ResolutionButton
              key={preset.id}
              resolution={preset}
              isSelected={value === preset.id}
              onClick={() => handleSelect(preset.id)}
              disabled={disabled}
            />
          ))}
          {allowCustom && (
            <ResolutionButton
              resolution={{
                id: 'custom',
                label: t('common.custom', 'Custom'),
                width: localCustomWidth,
                height: localCustomHeight,
              }}
              isSelected={isCustom}
              onClick={() => handleSelect('custom')}
              disabled={disabled}
            />
          )}
        </div>
      );
    }

    // Grouped mode - use dropdown for better UX with many options
    if (props.mode === 'grouped') {
      const allResolutions = (props.groups || []).flatMap((g) => g.resolutions || []);
      const selectedResolution = allResolutions.find((r) => r.id === value);

      return (
        <div className="space-y-3">
          <select
            value={isCustom ? 'custom' : value}
            onChange={(e) => {
              const id = e.target.value;
              if (id === 'custom') {
                handleSelect('custom');
              } else {
                const res = allResolutions.find((r) => r.id === id);
                if (res) {
                  handleSelect(id, { category: res.category });
                }
              }
            }}
            className="select w-full"
            disabled={disabled}
          >
            {(props.groups || []).map((group) => (
              <optgroup
                key={group.name}
                label={t(`resolution.categories.${group.name}`, group.name)}
              >
                {(group.resolutions || []).map((res) => (
                  <option key={res.id} value={res.id}>
                    {res.label} - {res.aspectRatio || `${res.width}x${res.height}`}
                  </option>
                ))}
              </optgroup>
            ))}
            {allowCustom && <option value="custom">{t('common.custom', 'Custom')}</option>}
          </select>

          {/* Selected resolution info */}
          {selectedResolution && !isCustom && showDimensionInfo && (
            <div className="flex items-center gap-3 p-3 bg-secondary-50 dark:bg-secondary-800 rounded-lg">
              {(() => {
                const IconComponent = CATEGORY_ICONS[selectedResolution.category || ''] || Square;
                return (
                  <IconComponent className="h-5 w-5 text-secondary-600 dark:text-secondary-400" />
                );
              })()}
              <div className="text-sm">
                <span className="font-medium text-secondary-900 dark:text-secondary-100">
                  {selectedResolution.width} x {selectedResolution.height}
                </span>
                {selectedResolution.aspectRatio && (
                  <span className="text-secondary-500 dark:text-secondary-400 ml-2">
                    ({selectedResolution.aspectRatio})
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <div className={cn('space-y-3', className)}>
      <label className="label-themed-bare">{t('resolution.label', 'Resolution')}</label>

      {renderPresets()}

      {/* Custom dimensions input */}
      {isCustom && (
        <div className="flex gap-3 items-center pt-2">
          <div className="flex-1">
            <label className="text-xs text-secondary-500 dark:text-secondary-400">
              {t('resolution.width', 'Width')}
            </label>
            <input
              type="number"
              min={constraints.minWidth}
              max={constraints.maxWidth}
              step={constraints.step}
              value={customWidth ?? localCustomWidth}
              onChange={(e) => handleCustomWidthChange(parseInt(e.target.value) || 0)}
              disabled={disabled}
              className="input w-full mt-1"
            />
          </div>
          <span className="text-secondary-400 mt-5">x</span>
          <div className="flex-1">
            <label className="text-xs text-secondary-500 dark:text-secondary-400">
              {t('resolution.height', 'Height')}
            </label>
            <input
              type="number"
              min={constraints.minHeight}
              max={constraints.maxHeight}
              step={constraints.step}
              value={customHeight ?? localCustomHeight}
              onChange={(e) => handleCustomHeightChange(parseInt(e.target.value) || 0)}
              disabled={disabled}
              className="input w-full mt-1"
            />
          </div>
        </div>
      )}

      {/* Dimension info for multiplier mode */}
      {props.mode === 'multipliers' && showDimensionInfo && currentDimensions && (
        <div className="text-xs text-secondary-500 dark:text-secondary-400">
          {(() => {
            const estimatedBytes = currentDimensions.width * currentDimensions.height * 3;
            const estimatedMB = bytesToMB(estimatedBytes).toFixed(1);
            return t('export.estimatedSize', `Estimated size: ~${estimatedMB} MB (uncompressed)`);
          })()}
        </div>
      )}
    </div>
  );
});

export default ResolutionSelector;
